/**
 * React hook that runs `fillForm` for the given formId, wraps the
 * result in a Blob, and triggers a browser download via the standard
 * `<a download>` click pattern.
 *
 * Lazy-loads `@inside-us-aos/pdf-engine` (and transitively `pdf-lib`,
 * ~600 KiB minified) only on the first click. The engine module is
 * cached after the first import so subsequent clicks resolve
 * synchronously. This keeps the host application's initial bundle in
 * the ~200–300 KiB-gzipped band.
 *
 * The dynamic-import pattern is the Vite-blessed code-split seam:
 *   https://vite.dev/guide/features.html#dynamic-import
 *
 * MDN sources for the runtime APIs used here:
 *   - URL.createObjectURL:
 *     https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
 *   - URL.revokeObjectURL:
 *     https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
 *   - Blob: https://developer.mozilla.org/en-US/docs/Web/API/Blob/Blob
 *   - HTMLAnchorElement.download:
 *     https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#download
 */
import { useCallback, useState } from 'react';
import type { Answer, FieldDefinition } from '@inside-us-aos/domain';
import type {
  FillDiagnostic,
  FormId,
} from '@inside-us-aos/pdf-engine';

import {
  type IMonitoringReporter,
  noopReporter,
} from './monitoring';

/**
 * Status surface for the download button. States are linear:
 *
 *   `idle` → `loading_engine` → `downloading` → (`success` | `error`)
 *
 * On a warm cache (subsequent clicks in the same session) the hook
 * skips `loading_engine` because the dynamic import resolves
 * synchronously from the module cache.
 */
export type DownloadStatus =
  | { kind: 'idle' }
  | { kind: 'loading_engine'; formId: FormId }
  | { kind: 'downloading'; formId: FormId }
  | {
      kind: 'success';
      formId: FormId;
      diagnostics: readonly FillDiagnostic[];
    }
  | { kind: 'error'; formId: FormId; message: string };

export interface DownloadFilledFormApi {
  status: DownloadStatus;
  download(formId: FormId): Promise<void>;
}

type PdfEngineModule = typeof import('@inside-us-aos/pdf-engine');
let pdfEnginePromise: Promise<PdfEngineModule> | null = null;
function loadPdfEngine(): Promise<PdfEngineModule> {
  if (pdfEnginePromise === null) {
    pdfEnginePromise = import('@inside-us-aos/pdf-engine');
  }
  return pdfEnginePromise;
}

/** Test seam — clears the cached pdf-engine module promise. */
export function __resetPdfEngineCacheForTests(): void {
  pdfEnginePromise = null;
}

export interface UseDownloadFilledFormArgs {
  catalog: readonly FieldDefinition[];
  getAnswers: () => readonly Answer[];
  /**
   * Defaults to `/forms`. The host app must serve the AcroForm
   * templates at `<templateBaseUrl>/<formId>.pdf`. The applicant-web
   * syncs them from `packages/domain/fixtures/forms/`; the admin-web
   * copies them at prebuild time.
   */
  templateBaseUrl?: string;
  /**
   * Injected monitoring reporter so the hook can stay in a shared
   * package without depending on either app's Sentry wiring. Defaults
   * to a no-op so callers can opt in only when they care.
   */
  reporter?: IMonitoringReporter;
}

/**
 * Build a download handler for a given catalog + answers source.
 * `getAnswers` is called fresh on every click so the button always
 * reflects the engine's current state.
 */
export function useDownloadFilledForm(
  args: UseDownloadFilledFormArgs,
): DownloadFilledFormApi {
  const {
    catalog,
    getAnswers,
    templateBaseUrl = '/forms',
    reporter = noopReporter,
  } = args;
  const [status, setStatus] = useState<DownloadStatus>({ kind: 'idle' });

  const download = useCallback(
    async (formId: FormId): Promise<void> => {
      const isCold = pdfEnginePromise === null;
      if (isCold) {
        setStatus({ kind: 'loading_engine', formId });
      } else {
        setStatus({ kind: 'downloading', formId });
      }
      const startedAtMs = Date.now();
      reporter.reportEvent('packet_download_initiated', {
        formId,
        startedAtMs,
      });
      try {
        const mod = await loadPdfEngine();
        if (isCold) {
          setStatus({ kind: 'downloading', formId });
        }
        const result = await mod.fillForm(formId, {
          catalog,
          answers: getAnswers(),
          templateLoader: mod.fetchTemplateLoader(templateBaseUrl),
        });
        triggerBlobDownload(result.bytes, `${formId}-filled.pdf`);
        setStatus({
          kind: 'success',
          formId,
          diagnostics: result.diagnostics,
        });
        reporter.reportEvent('packet_download_completed', {
          formId,
          startedAtMs,
          durationMs: Date.now() - startedAtMs,
          diagnosticCount: result.diagnostics.length,
          byteSize: result.bytes.length,
        });
      } catch (e) {
        setStatus({
          kind: 'error',
          formId,
          message: (e as Error).message,
        });
        reporter.reportEvent('packet_download_failed', {
          formId,
          startedAtMs,
          durationMs: Date.now() - startedAtMs,
          errorMessage: (e as Error).message,
        });
        reporter.reportError(e, {
          kind: 'pdf',
          flow: 'packet_download',
          formId,
        });
      }
    },
    [catalog, getAnswers, templateBaseUrl, reporter],
  );

  return { status, download };
}

/**
 * Programmatic download trigger. Creates a temporary `<a>`, clicks
 * it, and revokes the object URL. We append the anchor to the DOM
 * for Firefox compatibility (it ignores `.click()` on detached
 * anchors per MDN).
 */
function triggerBlobDownload(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
